import {
  readMfaRequiredClaim,
  confirmMfaRequirement,
  buildMfaEnrollmentUrl,
  isMfaTokenExpired,
  isMfaReleased,
  rememberMfaReleased
} from '~/utils/mfa'
import { markForcedRedirect } from '~/utils/emailVerification'

/**
 * Global route guard for Zoho-triggered forced MFA. Reads the signed
 * mfa_required claim from the id_token; on the rare positive it confirms with
 * rewrite-api (fail-closed) before doing anything visible, then hands a
 * genuinely-required user off to ig-console enrollment. A stale-positive claim
 * (already enrolled) proceeds with no redirect — the anti-bounce path.
 *
 * @param {object} ctx - Nuxt middleware context.
 * @param {object} ctx.$auth - auth-next instance (id_token + strategy).
 * @param {object} ctx.$axios - axios instance used for the requirement check.
 */
export default async function mfaRequired({ $auth, $axios }) {
  if (!$auth || !$auth.loggedIn) {
    return
  }

  const token = $auth.strategy.token.get()

  if (isMfaTokenExpired(token)) {
    // Expired/unusable session — hand off to the login flow. Running the check
    // on a dead token would 401 into a fail-closed console redirect the
    // chain-halt below makes inescapable.
    return
  }

  if (!readMfaRequiredClaim(token)) {
    // Overwhelming-majority path: no claim, no network, proceed.
    return
  }

  if (isMfaReleased(token)) {
    // Already confirmed released for this token in this tab — skip the repeat
    // requirement call. Keyed to the token, so a re-minted token re-verifies.
    return
  }

  // Claim says gated — confirm before anything visible so a stale claim never
  // causes a redirect.
  const required = await confirmMfaRequirement($axios)

  if (!required) {
    // Stale claim — remember the released verdict so later navigations on this
    // same token skip the check, then let the user through.
    rememberMfaReleased(token)
    return
  }

  const enrollmentUrl = buildMfaEnrollmentUrl()

  if (enrollmentUrl) {
    markForcedRedirect()
    window.location.replace(enrollmentUrl)
    // Halt the middleware chain: block the protected render and stop later
    // guards (termsIsRequired/validateAuth) from issuing a competing redirect
    // that would win last. Same never-resolving-promise idiom the axios
    // plugin uses after its auth redirect.
    return new Promise(() => {})
  }
}
